import { APIGet, APIPut, APIUrl, AdminType } from "api/protocols";
import { CancelToken } from "axios";
import { TFacilityTeeSheetSettings } from "redux/reducers/models/facility";

export interface IGetTeeSheetSettings {
  id: number;
}

export type TClockRoundOption = "no_round" | "round_5" | "10_round" | "15_round";

export interface IPutTeeSheetSettings {
  booking_terms: string;
  cart_waiver_content: string;
  close_date?: string | null;
  default_days_in_advance_start: number;
  default_days_in_advance_end: number;
  default_open_time: string; // time string
  default_open_structure: string;
  display_booking_graph: boolean;
  display_booking_source: boolean;
  display_customer_type: boolean;
  display_online_pricing: boolean;
  enable_web_booking: boolean;
  enable_cart_bookings: boolean;
  enable_tee_time_deposits: boolean;
  deposit_amount: string;
  deposit_application: string;
  foursome_bookings: string;
  max_booking_size?: number;
  open_date?: string | null;
  orders_void_with_note?: boolean;
  payment_terms: string;
  marketing_section: string;
  power_cart_maintenance_time: number;
  power_cart_nine_hole_time: number;
  power_cart_eighteen_hole_time: number;
  default_carts_available: number;
  single_bookings: string;
  tee_sheet_limited?: boolean;
  tee_sheet_new?: boolean;
  track_cart_numbers: boolean;
  use_cart_inventory: boolean;
  display_turn_division_code: boolean;
  threesome_bookings: string;
  twosome_bookings: string;
  web_cancellation_hours: number;
  web_edit_hours: number;
  validate_power_cart_count: boolean;
  gift_cards_default_auto_generate_code: boolean;
  gift_card_expiry?: "no_expiry" | "one_year" | "end_of_year" | "custom_years";
  gift_card_expiry_years?: number;
  web_tee_times_display: "list" | "tee_sheet" | null;
  rain_check_expiry: "one_year" | "end_of_year" | "no_expiry" | "custom_months";
  rain_check_expiry_months?: number;
  rain_check_description: string;
  booking_buffer: number;
  highlight_new_bookings: boolean;
  highlight_new_bookings_window: number;
  receipts_show_signature?: boolean;
  receipts_show_name?: boolean;
  receipts_show_member_number?: boolean;
  bookings_allow_guest_names: boolean;
  enable_service_fees: boolean;
  auto_charge_no_show: boolean;
  accounts_auto_pay_balance?: boolean;
  accounts_send_statement?: boolean;
  accounts_auto_pay_anchor_date?: number;
  accounts_accounting_reference_id?: number;
  tee_sheet_overview: boolean;
  tee_times_pre_paid_option: boolean;
  tee_times_access_code: boolean;
  send_maintenance_notifications: boolean;
  maintenance_email: string;
  receipts_url?: string;
  receipts_url_description?: string;
  line_items_stack?: boolean;
  enable_online_store_notifications?: boolean;
  online_store_notification_emails?: string;
  enable_internal_tee_sheet_confirmations?: boolean;
  internal_tee_sheet_confirmation_email?: string;
  enable_internal_reservation_confirmations?: boolean;
  internal_reservation_confirmation_email?: string;
  web_display_payment_option_account?: boolean;
  web_display_payment_option_gift_card?: boolean;
  web_display_payment_option_credit_book?: boolean;
  product_card_display_vendor?: boolean;
  product_card_display_inventory?: boolean;
  register_blind_close?: boolean;
  register_cash_round_change?: boolean;
  register_show_outstanding_balance?: boolean;
  pos_app_default_view?: "quick_sale" | "table_service";
  web_display_full_tee_times?: boolean;
  review_players_modal_customer_types?: Array<number>;
  booking_engine_display_waitlist?: boolean;
  table_service_clock_in?: boolean;
  table_service_send_chits?: boolean;
  prevent_multiple_bookings?: boolean;
  default_booking_holes?: number;
  online_store_all_products?: boolean;
  show_product_images?: boolean;
  invoice_terms?: string;
  auto_refund_pre_paid_bookings?: boolean;
  display_divisions_online?: boolean;
  auto_add_redeemed_tickets?: boolean;
  enable_booking_reminder_texts?: boolean;
  booking_reminder_hours?: number;
  enable_booking_reminder_emails?: boolean;

  //Edit on Staff Edit page
  //Used for rounding the clock in / clock out times
  clock_in_round_option?: TClockRoundOption;
  clock_out_round_option?: TClockRoundOption;
  clock_in_radius_meters?: number;

  //Restrict admin user to login near facility
  facility_login_radius_enabled?: boolean;
  facility_login_radius_meters?: string;
}

export interface IPutPrinterSettings {
  print_server_ip: null | string;
  print_server_port: null | number;
}

export async function GetFacilityTeeSheetSettings(
  params?: IGetTeeSheetSettings,
  useGlobalLoader?: boolean,
  token?: CancelToken,
) {
  return await APIGet<TFacilityTeeSheetSettings>(
    APIUrl(AdminType.FacilityAdmin, "/facility/settings/tee-sheet"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function PutFacilityTeeSheetSettings(params: IPutTeeSheetSettings, useGlobalLoader?: boolean) {
  return await APIPut<IPutTeeSheetSettings>(
    APIUrl(AdminType.FacilityAdmin, "/facility/settings/tee-sheet"),
    params,
    useGlobalLoader,
  );
}

export async function PutFacilityPrinterSettings(params: IPutPrinterSettings, useGlobalLoader?: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/facility/settings/print"), params, useGlobalLoader);
}

export async function GetFacilityPrinterSettings(useGlobalLoader?: boolean, token?: CancelToken) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/facility/settings/print"), undefined, useGlobalLoader, token);
}
